import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {
	DataCollectionStatus,
	Server,
} from "../../../../../services/model/server.model";
import {TranslateService} from "@ngx-translate/core";
import {Subscription} from "rxjs";
import {InfrastructureUtils} from "../../infrastructure.utils";
import {filter, tap} from "rxjs/operators";
import {ServerService} from "../../../../../services/back/server.service";
import {SnackbarService} from "../../../../../services/front/snackbar.service";
import {MatDialog} from "@angular/material/dialog";
import {DeleteDialogComponent} from "../../../../design-system/delete-dialog/delete-dialog.component";
import {ServerDetailData, ServerDetailService} from "../../../../../services/front/server-detail.service";
import {ActivatedRoute, Router} from "@angular/router";
import {ServerUrlService} from "../../../../../services/front/server-url.service";

@Component({
	selector: 'app-server-detail',
	standalone: false,
	templateUrl: './server-detail.component.html',
	styleUrl: './server-detail.component.scss'
})
export class ServerDetailComponent implements OnInit, OnDestroy {
	@Input() data: ServerDetailInput;

	@Output() onClose = new EventEmitter<void>();

	tenantId: string;
	isEditor: boolean;
	server: Server;

	_initializing: boolean;

	sliderIndex: number = 1;
	lazyLoadedTabs: boolean[] = [false, false, false];

	settings: boolean = false;

	subscriptions: Subscription = new Subscription();

	constructor(private serverDetailService: ServerDetailService,
				private serverUrlService: ServerUrlService,
				private activatedRoute: ActivatedRoute,
				private translate: TranslateService,
				private serverService: ServerService,
				private snackbar: SnackbarService,
				private dialog: MatDialog,
				private router: Router) {
	}

	ngOnInit() {
		this.router.navigate([], {queryParams: {serverId: this.data.serverId}, queryParamsHandling: 'merge'});

		this.sliderIndex = this.serverUrlService.getCurrentServerTab();
		this.lazyLoadedTabs[this.sliderIndex] = true;

		this.serverDetailService.initialize(this.data.serverId);

		this.subscriptions.add(this.serverDetailService.getInitializingChanges()
			.subscribe(initializing => this._initializing = initializing));

		this.subscriptions.add(this.serverDetailService.getServerDetailDataChanges()
			.pipe(tap(data => this.setServerDetailData(data)))
			.subscribe());

		this.subscriptions.add(this.activatedRoute.queryParams
			.subscribe(() => this.onQueryParamsChange()));
	}

	private setServerDetailData(data: ServerDetailData): void {
		this.tenantId = data.tenantId;
		this.isEditor = data.isEditor;
		this.server = data.server;
		if (data.resetTab) {
			this.settings = false;
			this.sliderIndex = 0;
			this.lazyLoadedTabs = [false, false, false];
			this.lazyLoadedTabs[0] = true;
			this.serverUrlService.changeCurrentServerTab(0);
		}
	}

	private onQueryParamsChange(): void {
		this.settings = this.serverUrlService.getParameter();
	}

	openDeleteServerPopup() {
		this.dialog.open(DeleteDialogComponent, {
			width: '475px',
			position: {
				right: '162.5px'
			},
			data: {
				icon: '/assets/illustrations/delete/infrastructure.svg',
				title: this.translate.instant('page.infrastructure.delete.title'),
				subtitle: this.translate.instant('page.infrastructure.delete.subtitle'),
				warningMessage: this.translate.instant('page.infrastructure.delete.warningMessage'),
				deleteButton: this.translate.instant('page.infrastructure.delete.deleteButton')
			}
		})
			.afterClosed()
			.pipe(
				filter(result => result)
			).subscribe(() => this.deleteServer());
	}

	close(): void {
		this.onClose.emit();
	}

	deleteServer() {
		this.serverService.deleteServer(this.tenantId, this.server.id)
			.subscribe(() => this.onClose.emit());
	}

	onOpenAppSettings(): void {
		this.serverUrlService.navigateInAndOutOfParameter(!this.settings);
	}

	onSaveSettings(): void {
		this.snackbar.show(this.translate.instant('page.application.detail.update.success'));
		this.serverUrlService.navigateInAndOutOfParameter(false);
	}

	onSliderChange(index: number): void {
		this.sliderIndex = index;
		this.lazyLoadedTabs[index] = true;
		this.serverUrlService.changeCurrentServerTab(index);
	}

	ngOnDestroy() {
		this.serverUrlService.changeCurrentServer(null);
		this.serverDetailService.clearServerDetailData();
		this.subscriptions.unsubscribe();
	}
}

export interface ServerDetailInput {
	serverId: string;
}
